/** A status word, worn as a pill.
 *
 * The tones are the design's, and they are the whole vocabulary: a published
 * track is accent, a pending one neutral, a held one warm, a failed check
 * red, and a circuit still being surveyed wears nothing but an outline of
 * type. Screens choose a tone, never a colour — that is the point of keeping
 * the five beds in `tokens.css` and none of them in a component.
 */

import type { ReactNode } from 'react';
import type { TrackState, UploadStatus, MergeRequestStatus } from '@/lib/types';

export type TagTone = 'accent' | 'neutral' | 'outline' | 'warn' | 'danger' | 'quiet';

export interface TagProps {
  children: ReactNode;
  tone?: TagTone;
  className?: string;
  title?: string;
}

const TONE_CLASS: Record<TagTone, string> = {
  accent: 'tag-accent',
  neutral: 'tag-neutral',
  outline: 'tag-outline',
  warn: 'tag-warn',
  danger: 'tag-danger',
  quiet: 'tag-quiet',
};

export function Tag({ children, tone = 'neutral', className, title }: TagProps) {
  return (
    <span className={['tag', TONE_CLASS[tone], className ?? ''].filter(Boolean).join(' ')} title={title}>
      {children}
    </span>
  );
}

/** The mapping the Tracks page and the uploads tables both need, written once
 *  so two screens cannot disagree about what "held" looks like. */
export function trackStateTone(state: TrackState): TagTone {
  switch (state) {
    case 'published':
      return 'accent';
    case 'pending':
      return 'neutral';
    case 'held':
      return 'warn';
    case 'surveying':
      return 'quiet';
  }
}

export function uploadStatusTone(status: UploadStatus): TagTone {
  switch (status) {
    case 'merged':
      return 'accent';
    case 'pending':
      return 'neutral';
    case 'held':
      return 'warn';
    case 'rejected':
      return 'danger';
  }
}

export function mergeRequestTone(status: MergeRequestStatus): TagTone {
  switch (status) {
    case 'auto_merged':
      return 'accent';
    case 'ci_failed':
      return 'danger';
    case 'closed':
      return 'quiet';
    case 'open':
    case 'awaiting_review':
      return 'neutral';
  }
}

/** `awaiting_review` reads badly in a table cell. */
export function mergeRequestLabel(status: MergeRequestStatus): string {
  switch (status) {
    case 'auto_merged':
      return 'auto-merged';
    case 'awaiting_review':
      return 'awaiting review';
    case 'ci_failed':
      return 'ci failed';
    case 'open':
      return 'open';
    case 'closed':
      return 'closed';
  }
}

export default Tag;
